/* Shared native-scroll treatment for EIDO's immersive sections. */
(() => {
  if (window.EidoScrollScenes) return;

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const scrollStep = 1.6;
  const holdFraction = .12;
  const parallaxFactor = .06;

  window.EidoScrollScenes = class EidoScrollScenes {
    constructor(root, { scenes, hydrate, activate, position, controls, fillSelector }) {
      this.root = root;
      this.scenes = scenes;
      this.hydrate = hydrate;
      this.activate = activate;
      this.position = position;
      this.controls = controls;
      this.fillSelector = fillSelector;
      this.viewport = root.querySelector('[data-eido-scroll-viewport]');
      this.events = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.active = -1;
      this.visible = new Set();
      this.prepared = new Set();
      this.description = root.getAttribute('aria-roledescription');
      root.eidoScrollScenes = this;
      root.style.setProperty('--eido-scroll-length', String(1 + Math.max(0, scenes.length - 1) * scrollStep));
      root.setAttribute('data-eido-parallax-ready', '');
      root.setAttribute('aria-roledescription', 'scrolling gallery');

      const on = (target, type, callback, options = {}) => target?.addEventListener(type, callback, {
        ...options, signal: this.events.signal
      });
      on(window, 'scroll', () => this.queue(), { passive: true });
      on(window, 'resize', () => this.queue(), { passive: true });
      on(window, 'pageshow', () => this.queue());
      on(this.motion, 'change', () => this.queue());
      // A fixed body preserves the gallery's visual position while a dialog is open.
      this.preferences = new MutationObserver(() => this.queue());
      this.preferences.observe(document.documentElement, {
        attributes: true, attributeFilter: ['data-eido-a11y-reduce-motion']
      });
      this.preferences.observe(document.body, { attributes: true, attributeFilter: ['style'] });
      this.resize = new ResizeObserver(() => this.queue());
      this.resize.observe(this.viewport);
      this.update();
    }

    reduced() {
      return this.motion.matches || document.documentElement.dataset.eidoA11yReduceMotion === 'true';
    }

    queue() {
      if (this.frame || this.events.signal.aborted) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    }

    geometry() {
      const rect = this.root.getBoundingClientRect();
      const height = this.viewport.clientHeight;
      return { rect, height, distance: Math.max(0, rect.height - height) };
    }

    prepare(index) {
      if (!this.scenes[index] || this.prepared.has(index)) return;
      this.hydrate(this.scenes[index]);
      this.prepared.add(index);
    }

    update() {
      if (!this.root.isConnected || document.body.style.position === 'fixed') return;
      const { rect, height, distance } = this.geometry();
      if (!height) return;
      const travel = distance ? clamp(-rect.top / distance, 0, 1) * (this.scenes.length - 1) : 0;
      const segment = Math.floor(travel);
      // Small scrolls at either end leave the scene still; the reveal eases between them.
      const fraction = clamp((travel - segment - holdFraction) / (1 - holdFraction * 2), 0, 1);
      const progress = segment + fraction * fraction * (3 - 2 * fraction);
      const reduced = this.reduced();
      const active = Math.round(progress);
      const first = reduced ? active : Math.floor(progress);
      const last = reduced ? active : Math.min(first + 1, this.scenes.length - 1);
      const reveal = reduced ? 0 : progress - first;
      const visible = new Set([first, last]);

      // Keep the previous panel pinned while the next opaque panel scrolls over it.
      visible.forEach(index => this.prepare(index));
      this.visible.forEach(index => {
        if (!visible.has(index)) this.scenes[index].removeAttribute('data-eido-scene-visible');
      });
      visible.forEach(index => {
        const scene = this.scenes[index];
        const offset = reduced || index === first ? 0 : (index - progress) * height;
        scene.setAttribute('data-eido-scene-visible', '');
        scene.style.setProperty('--eido-scene-shift', `${offset}px`);
        scene.style.setProperty('--eido-scene-layer', String(index === first ? 1 : 2));
        // Counter-motion gives the image depth while captions travel with the panel.
        scene.style.setProperty('--eido-media-shift', `${-offset * parallaxFactor}px`);
      });
      this.visible = visible;
      if (active !== this.active) {
        if (this.scenes[this.active]?.contains(document.activeElement)) {
          this.controls[active]?.focus({ preventScroll: true });
        }
        this.active = active;
        this.activate(active);
      }
      this.position();
      this.controls.forEach((control, index) => {
        control.setAttribute('aria-label', `Show ${this.scenes[index].dataset.title || this.scenes[index].dataset.label || 'scene'}, scene ${index + 1} of ${this.scenes.length}`);
        control.title = 'Scroll to scene';
        const fill = control.querySelector(this.fillSelector);
        if (fill) fill.style.transform = `scaleX(${reduced ? Number(index === active) : clamp(1 - Math.abs(index - progress), 0, 1)})`;
      });
      // Prepare the next poster only when this section approaches the viewport.
      if (reveal > .6 && !navigator.connection?.saveData && rect.top < innerHeight && rect.bottom > 0) this.prepare(last + 1);
    }

    goTo(index, { immediate = false } = {}) {
      index = clamp(index, 0, this.scenes.length - 1);
      this.prepare(index);
      const { rect, distance } = this.geometry();
      const top = scrollY + rect.top + (this.scenes.length > 1 ? distance * index / (this.scenes.length - 1) : 0);
      window.scrollTo({ top, behavior: immediate || this.reduced() ? 'instant' : 'smooth' });
      this.queue();
    }

    destroy() {
      this.events.abort();
      cancelAnimationFrame(this.frame);
      this.preferences.disconnect();
      this.resize.disconnect();
      this.root.removeAttribute('data-eido-parallax-ready');
      this.root.style.removeProperty('--eido-scroll-length');
      if (this.description === null) this.root.removeAttribute('aria-roledescription');
      else this.root.setAttribute('aria-roledescription', this.description);
      this.scenes.forEach(scene => {
        scene.removeAttribute('data-eido-scene-visible');
        ['shift', 'layer'].forEach(name => scene.style.removeProperty(`--eido-scene-${name}`));
        scene.style.removeProperty('--eido-media-shift');
      });
      delete this.root.eidoScrollScenes;
    }
  };
})();
